import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import { Link } from 'dibs-elements/exports/Link';

import styles from './styles/InteriorsLandingDesignerItem.scss';

import type { InteriorsLandingDesignerItem_designer$key } from './__generated__/InteriorsLandingDesignerItem_designer.graphql';
import type { DisplayType } from '../InteriorsLandingTypes';

type Props = {
    designer: InteriorsLandingDesignerItem_designer$key;
    type: DisplayType;
};

const profileUrl = (urlLabel: string): string => `/design-firms/${urlLabel}/`;

const InteriorsLandingDesignerItemComponent: FC<Props> = ({ designer: designerRef, type }) => {
    const designer = useFragment(
        graphql`
            fragment InteriorsLandingDesignerItem_designer on InteriorsLandingModuleItem {
                designFirm {
                    serviceId
                    name
                    directoryProfile {
                        profilePhoto
                    }
                    publicProfile {
                        urlLabel
                    }
                }
                imageUrl
            }
        `,
        designerRef
    );
    const { designFirm, imageUrl } = designer;
    const designerUrl = designFirm?.publicProfile?.urlLabel;
    const serviceId = designFirm?.serviceId;
    const designerName = designFirm?.name;
    const profileImage = designFirm?.directoryProfile?.profilePhoto;

    if (!designerUrl || !designerName) {
        return null;
    }

    let anchorCols = '';
    if (type === 'primary') {
        anchorCols = 'colXs12 colLg4';
    } else if (type === 'secondary') {
        anchorCols = 'colXs12 colLg6';
    } else if (type === 'tertiary') {
        anchorCols = `colXs6 colXl3 colLg4 colMd4`;
    }
    const anchorClasses = classnames(anchorCols, styles.designerItem, styles[type]);

    return (
        <Link
            href={profileUrl(designerUrl)}
            className={anchorClasses}
            dataTn={`interiors-landing-${type}-designer-tile-${serviceId}`}
            buyerLinkType={type === 'tertiary' ? 'standardLink' : undefined}
            underline={type === 'tertiary' ? 'none' : undefined}
        >
            <div
                className={classnames(styles.designerProjectImageWrapper, {
                    ['colXs6 colLg6 colXsCenter']: type === 'secondary',
                })}
            >
                {type !== 'tertiary' && imageUrl && (
                    <div
                        className={styles.designerProjectImage}
                        data-tn={`interiors-landing-${type}-designer-main-image`}
                        style={{ backgroundImage: `url(${imageUrl})` }}
                    />
                )}
                {profileImage && (
                    <div
                        className={styles.designerImageWrapper}
                        data-tn="interiors-landing-designer-profile-image"
                        style={{ backgroundImage: `url(${profileImage})` }}
                    >
                        {designerName}
                    </div>
                )}
            </div>

            <div
                className={classnames(styles.designerDetails, {
                    ['colXs6 colLg6 colXsCenter']: type === 'secondary',
                })}
            >
                {type !== 'tertiary' && (
                    <div
                        className={styles.designerTitle}
                        data-tn="interiors-landing-designer-title"
                    >
                        <FormattedMessage
                            id="abt.interiorsLanding.featuredDesigner"
                            defaultMessage="featured designer"
                        />
                    </div>
                )}

                <div className={styles.designerName} data-tn="interiors-landing-designer-name">
                    {designerName}
                </div>
            </div>
        </Link>
    );
};

export default InteriorsLandingDesignerItemComponent;
